/**
 * Uputstvo na samoj strani - objašnjenje koje se sklapa.
 *
 * Licence, paketi, mesta i cenovnik su sistem sa nekoliko pravila koja se **ne
 * vide iz tabele**: da je knjiga (obnova je nov red, ništa se ne prepisuje), da
 * paket samo prepisuje početne vrednosti, da mesta znače uređaje. Ko prvi put
 * otvori ekran vidi brojeve bez ijedne od tih rečenica i pogrešno zaključi.
 *
 * Zato objašnjenje stoji **tu gde se radi**, a ne u zasebnoj dokumentaciji koju
 * niko ne otvori usred posla. Otvoreno je pri prvom dolasku, a kad se sklopi,
 * ostaje sklopljeno - onome ko ovde radi svaki dan isti tekst je smetnja.
 */
import { useCallback, useEffect, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight } from 'lucide-react';

function citaj(kljuc: string): boolean {
  try {
    return localStorage.getItem(kljuc) !== 'sklopljeno';
  } catch {
    // Pregledač sa zabranjenim skladištem ne sme da obori stranu.
    return true;
  }
}

export function Uputstvo({ id, naslov, children }: {
  /** Ključ pod kojim se pamti da je sklopljeno - po strani, ne globalno. */
  id: string;
  naslov: string;
  children: React.ReactNode;
}) {
  const kljuc = `uputstvo:${id}`;
  const [otvoreno, setOtvoreno] = useState<boolean>(() => citaj(kljuc));

  useEffect(() => { setOtvoreno(citaj(kljuc)); }, [kljuc]);

  const prebaci = useCallback(() => {
    setOtvoreno((bilo) => {
      const sada = !bilo;
      try { localStorage.setItem(kljuc, sada ? 'otvoreno' : 'sklopljeno'); } catch { /* bez pamćenja */ }
      return sada;
    });
  }, [kljuc]);

  return (
    <section style={okvir} data-testid={`uputstvo-${id}`}>
      <button onClick={prebaci} style={zaglavlje} data-testid={`uputstvo-${id}-prekidac`}>
        <BookOpen size={14} style={{ flexShrink: 0 }} />
        <span style={{ fontWeight: 600, flex: 1, textAlign: 'left' }}>{naslov}</span>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          {otvoreno ? 'sakrij' : 'prikaži'}
        </span>
        {otvoreno ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {otvoreno && <div style={telo}>{children}</div>}
    </section>
  );
}

/** Jedan pojam i njegovo objašnjenje - da uputstvo ne bude zid teksta. */
export function Pojam({ naziv, children }: { naziv: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
      <span style={{
        minWidth: 132, fontWeight: 600, color: 'var(--color-text-strong)', fontSize: 12,
      }}>{naziv}</span>
      <span style={{ flex: 1, minWidth: 260 }}>{children}</span>
    </div>
  );
}

const okvir: React.CSSProperties = {
  background: 'var(--color-bg-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 10,
  marginBottom: 16,
  overflow: 'hidden',
};

const zaglavlje: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
  padding: '10px 14px', background: 'transparent', border: 'none',
  color: 'var(--color-text)', fontSize: 13, cursor: 'pointer',
};

const telo: React.CSSProperties = {
  padding: '4px 14px 16px',
  fontSize: 12.5,
  lineHeight: 1.65,
  color: 'var(--color-text-muted)',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  borderTop: '1px solid var(--color-border)',
};
